import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  Circle, 
  Sparkles, 
  Layout, 
  FileText, 
  Calendar, 
  Users, 
  LifeBuoy, 
  Settings, 
  ArrowRight, 
  X, 
  ChevronDown, 
  ChevronUp, 
  BookOpen, 
  Shield
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { cn } from '../../lib/utils';
import { useTenant } from '../../contexts/TenantContext';
import { DEFAULT_ENABLED_MODULES } from '../../constants';

const MODULE_NAMES: Record<string, string> = {
  PULSE: 'Pulse',
  DISCUSSIONS: 'Message Board',
  CALENDAR: 'Calendar',
  SUPPORT: 'Help Desk',
  DIRECTORY: 'Directory'
};

const coreModuleLabels = DEFAULT_ENABLED_MODULES.map(m => MODULE_NAMES[m] || m);
const coreModulesCount = DEFAULT_ENABLED_MODULES.length;
const coreModulesDisplayList = coreModuleLabels.length > 1
  ? `${coreModuleLabels.slice(0, -1).join(', ')}, and ${coreModuleLabels[coreModuleLabels.length - 1]}`
  : coreModuleLabels.join('');

interface GuideStep {
  id: string;
  title: string;
  moduleName: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  actionText: string;
  badge: string;
  tip: string;
}

interface WelcomeAboardGuideProps {
  onDismiss?: () => void;
  className?: string;
}

export const WelcomeAboardGuide: React.FC<WelcomeAboardGuideProps> = ({ onDismiss, className }) => {
  const { node, getTerm } = useTenant();
  const navigate = useNavigate();

  const nodeId = node?.id || 'default';
  const storageKey = `welcome_guide_completed_tasks_${nodeId}`;
  const dismissedKey = `welcome_guide_dismissed_${nodeId}`;

  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [showHandbookModal, setShowHandbookModal] = useState<boolean>(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(completedSteps));
    } catch (e) {
      console.warn('Failed to save welcome guide completed tasks', e);
    }
  }, [completedSteps, storageKey]);

  const toggleStepCompleted = (e: React.MouseEvent, stepId: string) => {
    e.stopPropagation();
    setCompletedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissedKey, 'true');
    } catch (e) {
      console.warn('Failed to save dismissal', e);
    }
    if (onDismiss) {
      onDismiss();
    }
  };

  const steps: GuideStep[] = [
    {
      id: 'step-pulse',
      title: `Broadcast Your First ${getTerm('pulse')}`,
      moduleName: 'Pulse',
      description: 'Publish an initial update or emergency announcement to your organization members and public feed.',
      icon: <Layout className="text-[#0044FF]" size={22} />,
      path: '/pulse',
      actionText: `Open ${getTerm('pulse')}`,
      badge: 'Core Module',
      tip: 'Pulse updates can be relayed to parent organizations or pushed as SMS alerts.'
    },
    {
      id: 'step-discussions',
      title: `Start a Topic on the ${getTerm('discussions')}`,
      moduleName: 'Message Board',
      description: 'Create threaded discussions for board matters, project planning, or community suggestions.',
      icon: <FileText className="text-[#FFD600]" size={22} />,
      path: '/discussions',
      actionText: 'Start Discussion',
      badge: 'Core Module',
      tip: 'Discussions support rich markdown, image embeds, and cascade from parent organizations.'
    },
    {
      id: 'step-calendar',
      title: 'Schedule Your First Event',
      moduleName: 'Calendar',
      description: 'Coordinate upcoming meetings, assemblies, or sessions with built-in RSVP tracking and calendar sync.',
      icon: <Calendar className="text-[#0044FF]" size={22} />,
      path: '/calendar',
      actionText: 'Open Calendar',
      badge: 'Core Module',
      tip: 'Events can be joined across parent and child chapters in joint operations.'
    },
    {
      id: 'step-directory',
      title: `Invite ${getTerm('member')}s & Assign Roles`,
      moduleName: 'Directory',
      description: 'Populate your organization roster, send email invitations, and define custom leadership roles with granular RBAC.',
      icon: <Users className="text-[#000000] dark:text-white" size={22} />,
      path: '/members?action=invite',
      actionText: `Invite ${getTerm('member')}s`,
      badge: 'Core Module',
      tip: 'Assign dynamic role priorities and capability flags tailored to your organization.'
    },
    {
      id: 'step-support',
      title: `Review Your ${getTerm('support')}`,
      moduleName: 'Help Desk',
      description: 'Provide an internal support ticketing queue where members can report issues, ask questions, or request assistance.',
      icon: <LifeBuoy className="text-[#0044FF]" size={22} />,
      path: '/support',
      actionText: `Go to ${getTerm('support')}`,
      badge: 'Core Module',
      tip: 'Tickets can be escalated upwards to parent councils if cross-tier assistance is required.'
    },
    {
      id: 'step-settings',
      title: 'Configure Power-Ups & Branding',
      moduleName: 'Settings',
      description: 'Customize your terminology, logo, primary colors, or unlock optional power-ups like Real-Time Chat, Ledger, and Vault.',
      icon: <Settings className="text-[#FFD600]" size={22} />,
      path: '/settings',
      actionText: 'Org Settings',
      badge: 'Customization',
      tip: 'Enable the Stripe Ledger for dues collection and Vault for legal document storage.'
    }
  ];

  const totalSteps = steps.length;
  const completedCount = steps.filter(s => completedSteps[s.id]).length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  return (
    <>
      <section 
        className={cn(
          "bg-white dark:bg-black border-8 border-black dark:border-white shadow-[12px_12px_0px_#0044FF] dark:shadow-[12px_12px_0px_#FFD600] relative overflow-hidden transition-all",
          className
        )}
      >
        {/* Top brutalist bar */}
        <div className="bg-black text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4 border-b-4 border-black dark:border-white">
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-[#FFD600]" />
            <span className="font-mono text-xs uppercase tracking-widest font-black">
              Welcome Aboard Guide • {node?.name || 'Organization'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHandbookModal(true)}
              className="text-[11px] font-mono uppercase tracking-widest text-white/90 hover:text-[#FFD600] flex items-center gap-1.5 transition-colors underline underline-offset-4"
            >
              <BookOpen size={14} /> Quick Handbook
            </button>
            <span className="text-white/40">|</span>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[11px] font-mono uppercase tracking-widest text-white/90 hover:text-white flex items-center gap-1 transition-colors"
            >
              {isExpanded ? (
                <>Collapse <ChevronUp size={14} /></>
              ) : (
                <>Expand <ChevronDown size={14} /></>
              )}
            </button>
            <span className="text-white/40">|</span>
            <button
              onClick={handleDismiss}
              aria-label="Dismiss Welcome Guide"
              className="text-white/80 hover:text-white hover:bg-white/20 p-1 transition-colors"
              title="Dismiss Guide"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Header content */}
        <div className="p-6 md:p-8 bg-slate-50 dark:bg-slate-950 border-b-4 border-black dark:border-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-3xl">
              <div className="flex items-center gap-2">
                <Badge className="bg-[#0044FF] text-white rounded-none border-2 border-black font-mono uppercase tracking-widest text-[10px]">
                  {coreModulesCount} Core Modules Active
                </Badge>
                <Badge className="bg-[#FFD600] text-black rounded-none border-2 border-black font-mono uppercase tracking-widest text-[10px]">
                  Tier: {node?.tier || 'LOCAL'}
                </Badge>
              </div>
              <h2 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Command Your Organization
              </h2>
              <p className="text-xs md:text-sm font-mono text-gray-700 dark:text-gray-300 max-w-2xl leading-relaxed">
                Your new organization has been pre-configured with <strong>{coreModulesDisplayList}</strong>. Complete the checklist below to jumpstart your organization.
              </p>
            </div>

            {/* Progress counter box */}
            <div className="bg-white dark:bg-black border-4 border-black dark:border-white p-4 min-w-[200px] shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex flex-col items-start md:items-end">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 font-bold mb-1">
                Setup Progress
              </div>
              <div className="text-3xl font-black font-display text-black dark:text-white">
                {completedCount} <span className="text-sm font-mono font-normal text-gray-500">/ {totalSteps}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-800 h-2 mt-2 border border-black dark:border-white overflow-hidden">
                <div 
                  className="bg-[#0044FF] h-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Steps Grid */}
        {isExpanded && (
          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-white dark:bg-black">
            {steps.map((step, idx) => {
              const isDone = Boolean(completedSteps[step.id]);

              return (
                <div
                  key={step.id}
                  className={cn(
                    "flex flex-col justify-between p-5 border-4 transition-all relative rounded-none text-left",
                    isDone 
                      ? "border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-[4px_4px_0px_#059669]" 
                      : "border-black dark:border-white bg-white dark:bg-slate-900 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFFFFF] hover:-translate-y-1 hover:shadow-[8px_8px_0px_#0044FF]"
                  )}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-black text-white font-mono text-[11px] font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500">
                          {step.moduleName}
                        </span>
                      </div>
                      
                      <button
                        onClick={(e) => toggleStepCompleted(e, step.id)}
                        className="p-1 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                        title={isDone ? "Mark Incomplete" : "Mark Complete"}
                      >
                        {isDone ? (
                          <CheckCircle2 className="text-emerald-600" size={20} />
                        ) : (
                          <Circle className="text-gray-400 hover:text-black dark:hover:text-white" size={20} />
                        )}
                      </button>
                    </div>

                    <div className="flex items-start gap-3 pt-1">
                      <div className="p-2 border-2 border-black dark:border-white bg-slate-100 dark:bg-slate-800">
                        {step.icon}
                      </div>
                      <div>
                        <h4 className="font-display font-black text-base uppercase tracking-tight text-black dark:text-white leading-snug">
                          {step.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-300 font-mono mt-1 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-5 mt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between gap-3">
                    <p className="text-[10px] font-mono text-gray-500 italic max-w-[160px] leading-tight">
                      {step.tip}
                    </p>
                    <Button
                      variant={isDone ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => navigate(step.path)}
                      className={cn(
                        "rounded-none font-bold uppercase tracking-wider text-[10px] py-2 px-3 gap-1.5 shadow-[2px_2px_0px_#000000]",
                        !isDone && "bg-black text-white hover:bg-[#0044FF]"
                      )}
                    >
                      {step.actionText} <ArrowRight size={12} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom bar with handbook CTA */}
        {isExpanded && (
          <div className="px-6 py-4 bg-[#FFD600] border-t-4 border-black dark:border-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-black">
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-black" />
              <p className="text-xs font-mono font-bold uppercase tracking-wider">
                Need more capabilities? You can enable Stripe Ledger payments, Chat, or Elections anytime.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowHandbookModal(true)}
                className="bg-white text-black border-2 border-black rounded-none uppercase font-bold text-[10px] tracking-wider py-2"
              >
                Read Handbook
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDismiss}
                className="bg-black text-white hover:bg-[#0044FF] rounded-none uppercase font-bold text-[10px] tracking-wider py-2"
              >
                Got It, Let's Go
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Quick Handbook Modal */}
      <Modal
        isOpen={showHandbookModal}
        onClose={() => setShowHandbookModal(false)}
        title="OrgSets New Organization Handbook"
        className="rounded-none border-8 border-black dark:border-white max-w-4xl"
        footer={
          <div className="flex justify-between items-center w-full">
            <span className="text-[11px] font-mono text-gray-500">
              OrgSets Platform Architecture Guide
            </span>
            <Button
              variant="primary"
              onClick={() => setShowHandbookModal(false)}
              className="bg-black text-white rounded-none uppercase font-bold text-xs"
            >
              Close Handbook
            </Button>
          </div>
        }
      >
        <div className="space-y-6 text-sm max-h-[70vh] overflow-y-auto pr-2 font-mono">
          <div className="p-4 bg-[#0044FF] text-white border-4 border-black space-y-1">
            <h4 className="font-display font-black text-xl uppercase tracking-tight">
              Essential Rules for Organization Owners
            </h4>
            <p className="text-xs opacity-90">
              OrgSets combines hierarchical multi-tier architecture with granular capability controls.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border-4 border-black dark:border-white bg-slate-50 dark:bg-slate-900 space-y-2">
              <h5 className="font-black text-xs uppercase tracking-widest text-[#0044FF] flex items-center gap-2">
                <Shield size={16} /> 1. Granular Role System
              </h5>
              <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                You are automatically the <strong>ADMIN</strong> of this organization. You can create custom roles (e.g. "Treasurer", "Captain", "Moderator") and grant fine-grained capabilities in the Directory.
              </p>
            </div>

            <div className="p-4 border-4 border-black dark:border-white bg-slate-50 dark:bg-slate-900 space-y-2">
              <h5 className="font-black text-xs uppercase tracking-widest text-[#0044FF] flex items-center gap-2">
                <Layout size={16} /> 2. {coreModulesCount} Core Modules Enabled
              </h5>
              <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                New organizations start with <strong>{coreModulesDisplayList}</strong>. Any module can be toggled in Org Settings &gt; Module Settings.
              </p>
            </div>

            <div className="p-4 border-4 border-black dark:border-white bg-slate-50 dark:bg-slate-900 space-y-2">
              <h5 className="font-black text-xs uppercase tracking-widest text-[#0044FF] flex items-center gap-2">
                <Users size={16} /> 3. Hierarchy & Sub-Organizations
              </h5>
              <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                If your plan supports sub-organizations, you can spawn child chapters and sub-squads. Parent updates and discussions seamlessly cascade down to child chapters.
              </p>
            </div>

            <div className="p-4 border-4 border-black dark:border-white bg-slate-50 dark:bg-slate-900 space-y-2">
              <h5 className="font-black text-xs uppercase tracking-widest text-[#0044FF] flex items-center gap-2">
                <Settings size={16} /> 4. Custom Terminology
              </h5>
              <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                Change terminology in Org Settings: rename "Member" to "Athlete" or "Brother", "Pulse" to "Gazette", and "Message Board" to "Forum" to match your group culture.
              </p>
            </div>
          </div>

          <div className="p-4 bg-[#FFD600] border-4 border-black text-black">
            <h5 className="font-black text-xs uppercase tracking-widest mb-1">
              Need help or have questions?
            </h5>
            <p className="text-xs leading-relaxed">
              Use your built-in Help Desk under <strong>/support</strong> or contact platform support at <strong>support@orgsets.com</strong>.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};
